import {
  StrapiShippingRate,
  ShippingType,
  SHIPPING_TYPES
} from '../core/models/strapi-shipping-rate.model';
import { strapiClient } from '../core/api/strapi-client';
import { regionStore } from '../store/jotai/store';
import { shippingRatesAtom } from '../store/jotai/atoms/shipping-rates.atom';
import { StrapiEntity } from '../constants/StrapiEntity';
import { StrapiVendor } from '../core/models/strapi-vendor.model';
import { StrapiResponse } from '../core/models/response/strapi-response.model';
import { Logger } from '../core/logger/Logger';
import { captureException } from '../utils/sentry';

const getShippingRates = async (): Promise<void> =>
  strapiClient.fetch<StrapiShippingRate[]>({ entityPath: 'shipping-rates' }).then((response) => {
    if (!response.data) {
      throw new Error('get shipping rates failed');
    }

    regionStore.set(shippingRatesAtom, response.data);
  });

const deleteShippingRate = async (id: number): Promise<void> =>
  strapiClient
    .delete(id, StrapiEntity.shippingRates)
    .then(() => {
      const currentRates = regionStore.get(shippingRatesAtom) || [];
      const updatedRates = currentRates.filter((rate) => rate.id !== id);
      regionStore.set(shippingRatesAtom, updatedRates);
    })
    .catch((error) => {
      Logger.error('Error deleting shipping rate:', error);
      captureException(error);
      throw new Error('Error deleting shipping rate');
    });

const setAllShippingRatesToNotDefault = async (isDefault: boolean, vendor: StrapiVendor) => {
  if (isDefault) {
    const rates = regionStore.get(shippingRatesAtom)?.filter((rate) => rate.isDefault);

    if (rates?.length) {
      // should only be one rate max but loop just in case
      for (const rate of rates) {
        await strapiClient.update(StrapiEntity.shippingRates, rate.id, {
          isDefault: false
        });
      }
    }
  }
};

const addNewShippingRate = async (item: Omit<StrapiShippingRate, 'id'>): Promise<void> => {
  await setAllShippingRatesToNotDefault(item.isDefault || false, item.vendor);

  const { data } = await strapiClient.create<
    Omit<StrapiShippingRate, 'vendor' | 'id'> & { vendor: number },
    StrapiResponse<StrapiShippingRate>
  >('shipping-rates', {
    title: item.title,
    rate: item.rate,
    isDefault: item.isDefault || false,
    shippingType: item.shippingType || SHIPPING_TYPES.PRODUCT,
    minOrderAmount: item.minOrderAmount || 0,
    vendor: item.vendor.id
  });

  if (!data) {
    throw new Error('addNewShippingRate failed');
  }

  const newShippingRate: StrapiShippingRate = {
    ...data.attributes,
    id: data.id,
    vendor: item.vendor
  };

  let updatedRates = regionStore.get(shippingRatesAtom) || [];
  updatedRates = updatedRates.map((el) => ({
    ...el,
    isDefault: item.isDefault ? false : el.isDefault
  }));
  updatedRates.unshift(newShippingRate);

  regionStore.set(shippingRatesAtom, updatedRates);
};

const updateShippingRate = async (
  id: number,
  title: string,
  rate: number,
  isDefault: boolean,
  vendor: StrapiVendor,
  shippingType?: ShippingType,
  minOrderAmount?: number
): Promise<void> => {
  await setAllShippingRatesToNotDefault(isDefault || false, vendor);

  const { data } = await strapiClient.update<
    Partial<StrapiShippingRate>,
    StrapiResponse<StrapiShippingRate>
  >(StrapiEntity.shippingRates, id, {
    title,
    rate,
    isDefault,
    shippingType: shippingType || SHIPPING_TYPES.PRODUCT,
    minOrderAmount: minOrderAmount || 0
  });

  if (!data) {
    throw new Error('updateShippingRate failed');
  }

  const currentRates = regionStore.get(shippingRatesAtom) || [];
  const updatedRates = currentRates.map((item) =>
    item.id === data.id
      ? { ...item, ...data.attributes }
      : { ...item, isDefault: isDefault ? false : item.isDefault }
  );

  regionStore.set(shippingRatesAtom, updatedRates);
};

export const shippingRateService = {
  getShippingRates,
  addNewShippingRate,
  updateShippingRate,
  deleteShippingRate
};

export type ShippingRateService = {
  getShippingRates: () => Promise<void>;
  addNewShippingRate: (item: StrapiShippingRate) => Promise<void>;
  updateShippingRate: (
    id: number,
    title: string,
    rate: number,
    isDefault: boolean,
    vendor: StrapiVendor,
    shippingType?: ShippingType,
    minOrderAmount?: number
  ) => Promise<void>;
  deleteShippingRate: (id: number) => Promise<void>;
};
